/**
 * Registration Step 4 - Player Registration Form (v2)
 *
 * Migrated to reusable wizard architecture.
 * - Receives data via props (registrationData)
 * - Updates parent via callbacks (onDataChange, onValidationChange)
 * - No context dependency - pure component
 */

import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, PartyPopper } from 'lucide-react';
import { FormInput } from '../../FormInput';
import { FormDatePicker } from '../../FormDatePicker';
import styles from './Step5PlayerForm.module.css';

const Step4PlayerForm = ({
  registrationData,
  onDataChange,
  onValidationChange
}) => {
  const data = registrationData.player;
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // Validation logic
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required fields
    if (!data.first_name?.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!data.last_name?.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!data.date_of_birth?.trim()) {
      newErrors.date_of_birth = 'Date of birth is required';
    }
    if (!data.gender?.trim()) {
      newErrors.gender = 'Gender is required';
    }
    if (!data.school?.trim()) {
      newErrors.school = 'School name is required';
    }
    if (!data.grade?.trim()) {
      newErrors.grade = 'Grade is required';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(isValid);
    }

    return isValid;
  }, [data, onValidationChange]);

  // Validate on data change
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    setIsDirty(true);

    onDataChange({
      ...registrationData,
      player: {
        ...data,
        [name]: value
      }
    });
  }, [data, registrationData, onDataChange]);

  const getFieldError = (fieldName) => errors[fieldName] || '';

  return (
    <div>
      <div className={styles.formHeader}>
        <h3 className={styles.formTitle}>
          Player Registration
        </h3>
        <p className={styles.formSubtitle}>
          Register your first player
        </p>
        {isDirty && (
          <div className={`${styles.validationStatus} ${Object.keys(errors).length === 0 ? styles.valid : styles.invalid}`}>
            {Object.keys(errors).length === 0 ? <><CheckCircle size={16} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} /> Form is valid</> : 'Please complete all required fields'}
          </div>
        )}
      </div>

      {/* Basic Information */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionHeading}>
          Basic Information
        </h4>

        <div className={styles.gridTwoColumn}>
          <FormInput
            label="First Name"
            name="first_name"
            value={data.first_name}
            onChange={handleInputChange}
            error={getFieldError('first_name')}
            required
            placeholder="Alex"
          />

          <FormInput
            label="Last Name"
            name="last_name"
            value={data.last_name}
            onChange={handleInputChange}
            error={getFieldError('last_name')}
            required
            placeholder="Smith"
          />
        </div>

        <div className={styles.gridTwoColumn}>
          <FormDatePicker
            label="Date of Birth"
            name="date_of_birth"
            value={data.date_of_birth}
            onChange={handleInputChange}
            error={getFieldError('date_of_birth')}
            required
            className={styles.dateField}
          />

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>
              Gender <span className={styles.requiredMark}>*</span>
            </label>
            <select
              name="gender"
              value={data.gender}
              onChange={handleInputChange}
              required
              className={`${styles.selectField} ${getFieldError('gender') ? styles.error : ''}`}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {getFieldError('gender') && (
              <div className={styles.fieldError}>
                {getFieldError('gender')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* School Information */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionHeading}>
          School Information
        </h4>

        <div className={styles.gridSchoolLayout}>
          <FormInput
            label="School Name"
            name="school"
            value={data.school}
            onChange={handleInputChange}
            error={getFieldError('school')}
            required
            placeholder="Lincoln Elementary School"
          />

          <FormInput
            label="Grade"
            name="grade"
            value={data.grade}
            onChange={handleInputChange}
            error={getFieldError('grade')}
            required
            placeholder="5th Grade"
          />
        </div>
      </div>

      {/* Soccer Experience */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionHeading}>
          Soccer Experience
        </h4>

        <div className={styles.fieldContainer}>
          <label className={styles.fieldLabel}>
            Previous Soccer Experience
          </label>
          <textarea
            name="previous_experience"
            value={data.previous_experience}
            onChange={handleInputChange}
            placeholder="Describe any previous soccer experience, teams played for, years of experience, etc. (Optional)"
            rows={4}
            className={styles.textareaField}
          />
        </div>
      </div>

      {/* Medical Information */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionHeading}>
          Medical Information
        </h4>

        <div className={styles.fieldContainer}>
          <label className={styles.fieldLabel}>
            Medical Conditions & Allergies
          </label>
          <textarea
            name="medical_conditions"
            value={data.medical_conditions}
            onChange={handleInputChange}
            placeholder="List any medical conditions, allergies, medications, or special needs we should be aware of. (Optional)"
            rows={3}
            className={styles.textareaField}
          />
        </div>
      </div>

      {/* Completion Message */}
      <div className={styles.completionBox}>
        <h4 className={styles.completionHeading}>
          <PartyPopper size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Almost Done!
        </h4>
        <p className={styles.completionMessage}>
          Next, you'll set up emergency contacts to complete your registration and access all programs.
        </p>
      </div>
    </div>
  );
};

export default Step4PlayerForm;
